import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/Career.css';

/* ══════════════════════════════════════
   GSAP LOADER
══════════════════════════════════════ */
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap && window.ScrollTrigger) { resolve(); return; }
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';
      s2.onload = () => { window.gsap.registerPlugin(window.ScrollTrigger); resolve(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  });
}

/* ══════════════════════════════════════
   PARTICLE CANVAS
══════════════════════════════════════ */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let W, H, pts, raf;
    const resize = () => { W = c.width = c.offsetWidth; H = c.height = c.offsetHeight; };
    const make = () => ({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - .5) * .25, vy: (Math.random() - .5) * .25, r: Math.random() * 1.3 + .3, a: Math.random() * .35 + .07 });
    const init = () => { resize(); pts = Array.from({ length: 55 }, make); };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => { p.x += p.vx; p.y += p.vy; if (p.x < 0) p.x = W; if (p.x > W) p.x = 0; if (p.y < 0) p.y = H; if (p.y > H) p.y = 0; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(47,128,237,${p.a})`; ctx.fill(); });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 90) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(47,128,237,${.1 * (1 - d / 90)})`; ctx.lineWidth = .5; ctx.stroke(); }
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    window.addEventListener('resize', init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', init); };
  }, []);
  return <canvas ref={ref} className="cr-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════ */
function SectionHeader({ eyebrow, title, accent, sub }) {
  return (
    <div className="cr-section-header">
      {eyebrow && <span className="cr-eyebrow">{eyebrow}</span>}
      <h2 className="cr-section-title">{title} {accent && <span className="cr-accent">{accent}</span>}</h2>
      {sub && <p className="cr-section-sub">{sub}</p>}
    </div>
  );
}

/* ══════════════════════════════════════
   SVG ICONS
══════════════════════════════════════ */
const IconRemote = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2"/>
    <path d="M8 24h32M24 8c-4 4-6 9-6 16s2 12 6 16M24 8c4 4 6 9 6 16s-2 12-6 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="24" cy="24" r="3" fill="currentColor" opacity="0.4"/>
  </svg>
);
const IconGrowth = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 38l8-10 8 4 10-16 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M34 14h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="6" y="40" width="36" height="2" rx="1" fill="currentColor" opacity="0.2"/>
  </svg>
);
const IconMission = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 6l4.5 9 9.5 1.5-7 6.8 1.7 9.7L24 28.5l-8.7 4.5 1.7-9.7L10 16.5 19.5 15z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M16 42h16M20 38v4M28 38v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconTeam = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="2"/>
    <circle cx="32" cy="16" r="6" stroke="currentColor" strokeWidth="2"/>
    <path d="M4 40c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M32 28c3.314 0 6 2.686 6 6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M44 40v-6a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconLearning = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 8L6 18l18 10 18-10-18-10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M12 24v10a12 5 0 0 0 24 0V24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M40 18v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="40" cy="32" r="2.5" stroke="currentColor" strokeWidth="1.8"/>
  </svg>
);
const IconImpact = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2"/>
    <circle cx="24" cy="24" r="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2"/>
    <circle cx="24" cy="24" r="3" fill="currentColor" opacity="0.6"/>
    <path d="M24 6v4M24 38v4M6 24h4M38 24h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.4"/>
  </svg>
);
const IconBriefcase = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M2 13h20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconLocation = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
    <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.8"/>
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

/* ══════════════════════════════════════
   ROLE CARD COMPONENT
══════════════════════════════════════ */
function RoleCard({ role }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`cr-role-card ${open ? 'cr-role-card--open' : ''}`} style={{ '--rc': role.color }}>
      <div className="cr-role-main" onClick={() => setOpen(!open)}>
        <div className="cr-role-left">
          <span className="cr-role-dept" style={{ color: role.color, background: role.color + '18', borderColor: role.color + '35' }}>
            {role.dept}
          </span>
          <h3 className="cr-role-title">{role.title}</h3>
          <div className="cr-role-meta">
            <span className="cr-meta-tag"><IconLocation /> {role.location}</span>
            <span className="cr-meta-tag"><IconBriefcase /> {role.type}</span>
            <span className="cr-meta-tag"><IconClock /> {role.exp}</span>
          </div>
        </div>
        <div className="cr-role-right">
          {role.hot && <span className="cr-hot-badge">🔥 Hot Role</span>}
          <button className="cr-expand-btn" aria-label="expand role">
            <span className="cr-expand-arrow">{open ? '↑' : '↓'}</span>
          </button>
        </div>
      </div>
      {open && (
        <div className="cr-role-details">
          <p className="cr-role-desc">{role.desc}</p>
          {role.skills && (
            <div className="cr-role-skills">
              {role.skills.map((s, i) => (
                <span key={i} className="cr-skill-pill" style={{ '--rc': role.color }}>{s}</span>
              ))}
            </div>
          )}
          <a href={`mailto:career@incogniflex.com?subject=Application: ${role.title}`} className="cr-apply-btn" style={{ '--rc': role.color }}>
            Apply Now <span>→</span>
          </a>
        </div>
      )}
      <div className="cr-role-accent" style={{ background: role.color }} aria-hidden />
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const Careers = () => {
  const pageRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('All');

  const perks = [
    { icon: <IconRemote />,   color: 'var(--blue)',   title: 'Remote-First Culture',    desc: 'Work from anywhere in the world. We believe in async-first, results-driven collaboration.' },
    { icon: <IconGrowth />,   color: 'var(--cyan)',   title: 'Growth & Impact',         desc: 'Join an early-stage company where your work directly shapes the product and direction.' },
    { icon: <IconLearning />, color: 'var(--purple)', title: 'Learning Budget',         desc: 'From AI research to operations excellence — we fund courses, conferences, and mentorship.' },
    { icon: <IconTeam />,     color: 'var(--green)',  title: 'People-First Team',       desc: 'Flat hierarchy, transparent communication, and genuine care for work-life balance.' },
    { icon: <IconMission />,  color: 'var(--amber)',  title: 'Meaningful Mission',      desc: 'Build technology that makes real-world operations smarter and more human — not less.' },
    { icon: <IconImpact />,   color: 'var(--pink)',   title: 'Real Ownership',          desc: 'You won\'t be a cog here. Own entire features, drive decisions, and see your name ship.' },
  ];

  const openRoles = [
    {
      title: 'Senior Machine Learning Engineer',
      dept: 'AI / ML', color: '#2f80ed', hot: true,
      location: 'Remote (India / Global)',
      type: 'Full-Time', exp: '3–6 yrs',
      desc: 'Design, build, and deploy production-grade ML models across our AIaaS platform. You\'ll work closely with product and data teams to ship models that solve real business problems.',
      skills: ['Python', 'PyTorch', 'MLOps', 'AWS SageMaker', 'LLMs', 'FastAPI'],
    },
    {
      title: 'NLP Research Engineer',
      dept: 'AI / ML', color: '#2f80ed',
      location: 'Remote (India preferred)',
      type: 'Full-Time', exp: '2–5 yrs',
      desc: 'Push the boundaries of language models for domain-specific enterprise use cases. Research, fine-tune, and integrate NLP capabilities into our product pipeline.',
      skills: ['NLP', 'Transformers', 'Hugging Face', 'Python', 'RLHF', 'LangChain'],
    },
    {
      title: 'Cloud Infrastructure Engineer',
      dept: 'Cloud', color: '#56ccf2', hot: true,
      location: 'Remote',
      type: 'Full-Time', exp: '3–5 yrs',
      desc: 'Design and maintain our multi-cloud infrastructure across AWS and GCP. Own reliability, scalability, and cost optimisation as we scale AIaaS workloads globally.',
      skills: ['AWS', 'GCP', 'Kubernetes', 'Terraform', 'Docker', 'CI/CD'],
    },
    {
      title: 'Full-Stack Product Engineer',
      dept: 'Engineering', color: '#7c6fe0',
      location: 'Remote',
      type: 'Full-Time', exp: '2–4 yrs',
      desc: 'Build the interfaces and APIs that customers interact with daily. Work across React frontends and Node/Python backends, shipping features with full ownership.',
      skills: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'REST APIs', 'AWS'],
    },
    {
      title: 'AI Operations Analyst',
      dept: 'Operations', color: '#10b981',
      location: 'Bengaluru / Remote',
      type: 'Full-Time', exp: '1–3 yrs',
      desc: 'Support and optimise our HaaS operations. Work alongside AI pipelines to review outputs, improve quality, and mentor junior ops team members.',
      skills: ['Data QA', 'Process Ops', 'Annotation', 'Excel/Sheets', 'Communication'],
    },
    {
      title: 'Customer Success Manager',
      dept: 'Customer', color: '#f59e0b',
      location: 'Remote (IST timezone)',
      type: 'Full-Time', exp: '2–4 yrs',
      desc: 'Own onboarding and retention for our enterprise clients. Build deep relationships, diagnose pain points, and coordinate with product to drive expansion revenue.',
      skills: ['CRM', 'Onboarding', 'SaaS Metrics', 'B2B', 'Communication', 'Empathy'],
    },
    {
      title: 'Data Annotation Team Lead',
      dept: 'Operations', color: '#10b981',
      location: 'Bengaluru / Remote',
      type: 'Full-Time', exp: '2–4 yrs',
      desc: 'Lead a team of annotators and QA specialists delivering high-accuracy training data for AI/ML models. Define workflows, SLAs, and quality benchmarks.',
      skills: ['Team Leadership', 'Data Labeling', 'QA', 'Process Design', 'NLP basics'],
    },
    {
      title: 'AI Ethics & Policy Researcher',
      dept: 'Research', color: '#ec4899',
      location: 'Remote',
      type: 'Contract / FTE', exp: '2–5 yrs',
      desc: 'Help us build AI responsibly. Research ethical implications of our AI products, advise on bias mitigation strategies, and contribute to our responsible AI framework.',
      skills: ['AI Ethics', 'Policy Research', 'Technical Writing', 'Fairness/Bias', 'Philosophy'],
    },
  ];

  const depts = ['All', ...Array.from(new Set(openRoles.map(r => r.dept)))];
  const filteredRoles = activeFilter === 'All' ? openRoles : openRoles.filter(r => r.dept === activeFilter);

  const testimonials = [
    { quote: 'At IncogniFlex, I get to work on real AI problems while staying connected to a mission that values human input. Best of both worlds.', name: 'Ravi', role: 'Machine Learning Engineer', avatar: 'R', color: '#2f80ed' },
    { quote: 'Even as a remote team member, I feel seen and supported. The culture is genuinely people-first.', name: 'Ananya', role: 'Customer Ops Specialist', avatar: 'A', color: '#10b981' },
    { quote: 'The learning budget is real. I\'ve attended two conferences this year and my manager actively pushes me to grow beyond my current role.', name: 'Priya', role: 'NLP Engineer', avatar: 'P', color: '#7c6fe0' },
  ];

  const values = [
    { emoji: '🫶', label: 'People First',     desc: 'We respect, trust, and uplift each other.' },
    { emoji: '⚡', label: 'Built for Change',  desc: 'Flexibility isn\'t just in our name — it\'s in our DNA.' },
    { emoji: '🎯', label: 'Own It',            desc: 'We take responsibility, and we celebrate wins together.' },
    { emoji: '⚖️', label: 'Ethics Matter',     desc: 'We build with purpose and integrity.' },
  ];

  useEffect(() => {
    loadGSAP().then(() => {
      const { gsap, ScrollTrigger } = window;

      gsap.fromTo('.cr-hero-content > *',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.2 }
      );

      gsap.utils.toArray('.cr-section-header').forEach(el => {
        gsap.fromTo(el,
          { opacity: 0, y: 32 },
          { opacity: 1, y: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%' }
          }
        );
      });

      gsap.fromTo('.cr-perk-card',
        { opacity: 0, y: 50, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.09, ease: 'power3.out',
          scrollTrigger: { trigger: '.cr-perks-grid', start: 'top 80%' }
        }
      );

      gsap.fromTo('.cr-role-card',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: { trigger: '.cr-roles-list', start: 'top 80%' }
        }
      );

      gsap.utils.toArray('.cr-testimonial-card').forEach((el, i) => {
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -40 : 40 },
          { opacity: 1, x: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 82%' }
          }
        );
      });

      gsap.fromTo('.cr-value-card',
        { opacity: 0, y: 40, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.55, stagger: 0.1, ease: 'back.out(1.3)',
          scrollTrigger: { trigger: '.cr-values-grid', start: 'top 80%' }
        }
      );

      gsap.fromTo('.cr-cta-inner > *',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.cr-cta-section', start: 'top 82%' }
        }
      );

      gsap.fromTo('.cr-process-step',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.55, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.cr-process-grid', start: 'top 80%' }
        }
      );
    });

    return () => {
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  const hiringSteps = [
    { num: '01', title: 'Apply',         desc: 'Send us your resume and a short note about why you\'re interested.' },
    { num: '02', title: 'Intro Call',    desc: '30-minute chat with our talent team to align on role and expectations.' },
    { num: '03', title: 'Skills Round',  desc: 'A practical assignment or technical interview — no whiteboard puzzles.' },
    { num: '04', title: 'Team Interview',desc: 'Meet your future teammates and get a feel for how we work.' },
    { num: '05', title: 'Offer',         desc: 'We move fast. Expect a decision within 5–7 business days.' },
  ];

  return (
    <div className="cr-page" ref={pageRef}>

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="cr-hero">
        <ParticleField />
        <div className="cr-grid-bg" aria-hidden />
        <div className="cr-glow cr-glow-l" aria-hidden />
        <div className="cr-glow cr-glow-r" aria-hidden />
        <div className="cr-hero-inner">
          <div className="cr-hero-content">
            <span className="cr-hero-eyebrow">
              <span className="cr-eyebrow-dot" />
              Careers at IncogniFlex · Join the Team
            </span>
            <h1 className="cr-hero-title">
              Build the Future of{' '}
              <span className="cr-hero-accent">AI + Human</span>
            </h1>
            <p className="cr-hero-sub">
              We're a World Class team of engineers, researchers, and operators on a mission to
              make AI more human — and humans more powerful. Come build with us.
            </p>
            <div className="cr-hero-tags">
              {['Remote-First', 'AI-Driven', 'People-First', 'Fast-Moving'].map(t => (
                <span key={t} className="cr-hero-tag">{t}</span>
              ))}
            </div>
            <div className="cr-hero-actions">
              <a href="#open-roles" className="cr-cta-btn primary">
                <span>See Open Roles</span>
                <span className="cr-btn-arrow">↓</span>
              </a>
              <a href="mailto:career@incogniflex.com" className="cr-cta-btn secondary">
                Send Open Application
              </a>
            </div>
          </div>
        </div>
        <div className="cr-hero-cut" aria-hidden />
      </section>

      {/* ════════════════════ WHY COGNI ════════════════════ */}
      <section className="cr-perks-section">
        <div className="cr-container">
          <SectionHeader
            eyebrow="✨ Why IncogniFlex"
            title="Where You'll"
            accent="Actually Thrive"
            sub="Real perks, real culture. We've built the kind of workplace we always wanted to work at."
          />
          <div className="cr-perks-grid">
            {perks.map((p, i) => (
              <div key={i} className="cr-perk-card" style={{ '--pc': p.color }}>
                <div className="cr-perk-icon-wrap" style={{ borderColor: p.color + '44', background: p.color + '15', color: p.color }}>
                  {p.icon}
                </div>
                <h3 className="cr-perk-title">{p.title}</h3>
                <p className="cr-perk-desc">{p.desc}</p>
                <div className="cr-perk-glow" style={{ background: p.color }} aria-hidden />
                <div className="cr-perk-top-line" style={{ background: `linear-gradient(90deg, ${p.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ OPEN ROLES ════════════════════ */}
      <section className="cr-roles-section" id="open-roles">
        <div className="cr-container">
          <SectionHeader
            eyebrow="📌 Open Roles"
            title="Find Your"
            accent="Next Role"
            sub="We're hiring across engineering, AI, operations, and more. All roles are remote-friendly."
          />

          {/* Filter Tabs */}
          <div className="cr-filter-tabs">
            {depts.map(d => (
              <button key={d} className={`cr-filter-tab ${activeFilter === d ? 'cr-filter-tab--active' : ''}`} onClick={() => setActiveFilter(d)}>
                {d}
              </button>
            ))}
          </div>

          <div className="cr-roles-list">
            {filteredRoles.map((role, i) => (
              <RoleCard key={i} role={role} />
            ))}
          </div>

          {/* Open application callout */}
          <div className="cr-open-apply">
            <span className="cr-open-apply-pin">📌</span>
            <div>
              <p className="cr-open-apply-text">
                Don't see a role that fits? Send your resume anyway — we're always looking for curious minds.
              </p>
              <a href="mailto:career@incogniflex.com" className="cr-open-apply-link">
                📧 career@incogniflex.com →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ HIRING PROCESS ════════════════════ */}
      <section className="cr-process-section">
        <div className="cr-container">
          <SectionHeader
            eyebrow="⚡ Our Hiring Process"
            title="Simple, Fast,"
            accent="Human"
            sub="No puzzles. No weeks of silence. We respect your time and keep things transparent."
          />
          <div className="cr-process-grid">
            {hiringSteps.map((step, i) => (
              <div key={i} className="cr-process-step">
                <div className="cr-proc-num">{step.num}</div>
                <div className="cr-proc-connector" aria-hidden />
                <h4 className="cr-proc-title">{step.title}</h4>
                <p className="cr-proc-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ TESTIMONIALS ════════════════════ */}
      <section className="cr-testimonials-section">
        <div className="cr-container">
          <SectionHeader
            eyebrow="💬 What Our Team Says"
            title="Straight From"
            accent="The People"
            sub="Don't take our word for it — hear from the team building IncogniFlex every day."
          />
          <div className="cr-testimonials-grid">
            {testimonials.map((t, i) => (
              <div key={i} className="cr-testimonial-card" style={{ '--tc': t.color }}>
                <div className="cr-quote-mark">"</div>
                <p className="cr-quote-text">{t.quote}</p>
                <div className="cr-quote-author">
                  <div className="cr-author-avatar" style={{ background: t.color + '28', borderColor: t.color + '55', color: t.color }}>
                    {t.avatar}
                  </div>
                  <div>
                    <span className="cr-author-name">{t.name}</span>
                    <span className="cr-author-role">{t.role}</span>
                  </div>
                </div>
                <div className="cr-test-glow" style={{ background: t.color }} aria-hidden />
                <div className="cr-test-top-line" style={{ background: `linear-gradient(90deg, ${t.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ VALUES ════════════════════ */}
      <section className="cr-values-section">
        <div className="cr-container">
          <SectionHeader
            eyebrow="🙌 Our Values"
            title="What We"
            accent="Stand For"
            sub="Four principles that guide every decision, every hire, every product we build."
          />
          <div className="cr-values-grid">
            {values.map((v, i) => (
              <div key={i} className="cr-value-card">
                <div className="cr-value-emoji">{v.emoji}</div>
                <h3 className="cr-value-label">{v.label}</h3>
                <p className="cr-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="cr-cta-section">
        <div className="cr-cta-glow" aria-hidden />
        <div className="cr-cta-grid-bg" aria-hidden />
        <div className="cr-container">
          <div className="cr-cta-inner">
            <span className="cr-cta-eyebrow">
              <span className="cr-eyebrow-dot" />
              Ready to Join?
            </span>
            <h2 className="cr-cta-title">
              Let's Build <span className="cr-accent">Together</span>
            </h2>
            <p className="cr-cta-sub">
              Whether you're a seasoned engineer or an early-career talent with fire in your eyes — we want to meet you.
            </p>
            <div className="cr-cta-actions">
              <a href="#open-roles" className="cr-cta-btn primary">
                <span>View Open Roles</span>
                <span className="cr-btn-arrow">→</span>
              </a>
              <a href="mailto:career@incogniflex.com" className="cr-cta-btn secondary">
                Send Open Application
              </a>
            </div>
            <p className="cr-cta-note">career@incogniflex.com · Response within 48 hours</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Careers;